import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import ModuleCard from '../components/ModuleCard';
import { getModules } from '../lib/apiClient';
import { getStudyHub } from '../lib/studyApi';
import { useAuth } from '../context/AuthContext';

/**
 * Faculty / Developer: shows what a student would see. Only published modules.
 * Reuses ModuleCard with viewerRolePath="/preview-as-student" so links route
 * through the same viewer with student-style locking applied.
 * Exit-preview link routes by current real role.
 */
const StudentPreview = () => {
  const { user } = useAuth();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decksByModuleId, setDecksByModuleId] = useState({});

  // Where "Exit preview" should land
  const exitPath = user?.role === 'developer' ? '/developer'
                 : user?.role === 'institution_admin' ? '/institution_admin'
                 : '/teacher';

  useEffect(() => {
    getModules()
      .then((all) => setModules(all.filter((m) => m.status === 'published')))
      .finally(() => setLoading(false));
    getStudyHub()
      .then((hub) => {
        const byId = {};
        for (const d of (hub.decks || [])) {
          if (d.module_id) byId[d.module_id] = d;
        }
        setDecksByModuleId(byId);
      })
      .catch(() => setDecksByModuleId({}));
  }, []);

  return (
    <div className="min-h-screen" data-testid="student-preview">
      <Navbar />
      {/* Preview banner */}
      <div className="bg-amber-50 border-b border-amber-200">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between gap-3">
          <div className="text-sm text-amber-900">
            <span className="font-semibold">Preview as Student</span>
            <span className="ml-2 opacity-80">— showing only published modules. Drafts are hidden.</span>
          </div>
          <Link to={exitPath} className="text-xs font-semibold underline text-amber-900"
                data-testid="exit-preview-link">
            Exit preview ↩
          </Link>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Your Modules</h1>
          <p className="text-gray-500 mt-1">This is exactly what enrolled students see.</p>
        </div>
        {loading ? (
          <div className="text-gray-500">Loading…</div>
        ) : modules.length === 0 ? (
          <div className="card p-8 text-center text-gray-500">
            No published modules yet. Publish a module in the Developer Console to see it here.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="preview-modules-grid">
            {modules.map((m) => (
              <ModuleCard key={m.id} module={m} viewerRolePath="/preview-as-student"
                          studyDeck={decksByModuleId[m.id] || null} />
            ))}
          </div>
        )}
      </main>
      {/* AI Tutor is provided by the app-level <AITutorLauncher />. */}
    </div>
  );
};

export default StudentPreview;
